import { useCallback } from "react";
import styled from "@emotion/styled";
import { ItemListItem, ItemListItemKind } from "@/components/item-list/types";
import {
  IMDSIconButtonProps,
  MDSIconButton,
  MDSIconButtonVariant,
  MDSIconKind,
  MDSRowItemAppearance,
  MDSRowItemSlotName,
  useTheme,
} from "@/mds";
import { useThemedCollectionColors } from "@/mds/themes/use-themed-collection-colors";
import { AudioCaptureWaveForm } from "@/components/layout/components/audio-capture";

interface ItemListToggleIconProps {
  item: ItemListItem;
  isSelected?: boolean;
  isHighlighted?: boolean;
  isToggleable?: boolean;
  itemRef: React.MutableRefObject<ItemListItem>;
  handleItemClick: (event: React.MouseEvent) => void;
  slot?: MDSRowItemSlotName;
}

export function ItemListToggleIcon({
  item,
  itemRef,
  isSelected,
  isHighlighted,
  handleItemClick,
}: ItemListToggleIconProps) {
  const { theme } = useTheme();

  const toggleSelected = useCallback(
    (event: React.MouseEvent) => {
      if (event.shiftKey) {
        handleItemClick(event);
        return;
      }
      event.stopPropagation();
      const item = itemRef.current;
      item.listState?.toggleItemSelection(item.id);
    },
    [handleItemClick, itemRef]
  );

  const { iconColor: collectionIconColor } = useThemedCollectionColors(item.id);
  const getIconKind = ({
    isSelected,
    isHighlighted,
    defaultIcon,
  }: {
    isSelected?: boolean;
    isHighlighted?: boolean;
    defaultIcon: IMDSIconButtonProps["iconKind"];
  }) => {
    if (isSelected) {
      return MDSIconKind.CheckboxSolid;
    }

    if (isHighlighted) {
      return MDSIconKind.CheckboxEmpty;
    }

    return defaultIcon;
  };

  return (
    <StyledWrapper>
      {item.kind === ItemListItemKind.Collection && (
        <StyledIconButton
          onClick={toggleSelected}
          variant={MDSIconButtonVariant.Transparent}
          iconKind={getIconKind({ isSelected, isHighlighted, defaultIcon: MDSIconKind.Collection })}
          $isSelected={isSelected}
          $isHighlighted={isHighlighted}
          $iconColor={collectionIconColor}
        />
      )}
      {item.kind === ItemListItemKind.Note && (
        <StyledIconButton
          onClick={toggleSelected}
          variant={MDSIconButtonVariant.Transparent}
          iconKind={getIconKind({
            isSelected,
            isHighlighted,
            defaultIcon:
              item.appearance === MDSRowItemAppearance.AudioRecordingRecording
                ? () => <AudioCaptureWaveForm associatedNoteId={item.id} />
                : MDSIconKind.Document,
          })}
          $isSelected={isSelected}
          $isHighlighted={isHighlighted}
        />
      )}
      {item.kind === ItemListItemKind.Template && (
        <StyledIconButton
          onClick={toggleSelected}
          variant={MDSIconButtonVariant.Transparent}
          iconKind={getIconKind({ isSelected, isHighlighted, defaultIcon: MDSIconKind.Template })}
          $isSelected={isSelected}
          $isHighlighted={isHighlighted}
        />
      )}
      {item.kind === ItemListItemKind.AudioRecording && (
        <StyledIconButton
          onClick={toggleSelected}
          variant={MDSIconButtonVariant.Transparent}
          iconKind={getIconKind({ isSelected, isHighlighted, defaultIcon: MDSIconKind.Microphone })}
          $isSelected={isSelected}
          $isHighlighted={isHighlighted}
        />
      )}
      {item.kind === ItemListItemKind.Search && (
        <StyledIconButton
          onClick={toggleSelected}
          variant={MDSIconButtonVariant.Transparent}
          iconKind={MDSIconKind.Search} // Not toggleable
          $isSelected={isSelected}
          $isHighlighted={isHighlighted}
          $iconColor={theme.colors.icon.emphasis}
        />
      )}
      {item.kind === ItemListItemKind.Message && (
        <StyledIconButton
          onClick={toggleSelected}
          variant={MDSIconButtonVariant.Transparent}
          iconKind={MDSIconKind.Message} // Not toggleable
          $isSelected={isSelected}
          $isHighlighted={isHighlighted}
          $iconColor={theme.colors.icon.emphasis}
        />
      )}
    </StyledWrapper>
  );
}

const StyledWrapper = styled.div`
  display: flex;
  flex-direction: row;
  flex-wrap: nowrap;
  justify-content: center;
  min-width: fit-content;
  align-self: stretch;
  padding-right: ${({ theme }) => theme.spacing["3"]};
  padding-top: 2px;
`;

const StyledIconButton = styled(MDSIconButton)<{
  $isSelected?: boolean;
  $isHighlighted?: boolean;
  $iconColor?: string;
}>`
  padding: 0px;
  height: 16px;
  width: 16px;

  .mds-icon {
    font-size: 18px;
    height: 18px;
    width: 18px;
    color: ${
      ({ $isSelected, $isHighlighted, $iconColor, theme }) =>
        $isSelected
          ? theme.colors.icon.selected // Selected state
          : $isHighlighted
            ? theme.colors.icon.placeholder // Highlighted state
            : $iconColor
              ? $iconColor // Optional color override
              : theme.colors.icon.placeholder // Base icon color
    };
    transition: all 0.3s cubic-bezier(0.34, 1.56, 0.64, 1);
  }
`;
